import { FC, useState } from "react";

import {
  ActivityIcon,
  ArrowRightIcon,
  Box,
  Button,
  CloseIcon,
  Column,
  DrawerBody,
  DrawerHeader,
  IconButton,
  Row,
  SectionHeading,
  Spinner,
  Text,
  useDisclosure,
  TextWithTooltip,
} from "@hightouch/htui";
import { formatDistance, parseISO, subDays } from "date-fns";

import { RouterLink } from "src/router";
import { useHomeActivityFeedQuery } from "src/graphql";
import { Drawer } from "src/components/drawer";
import { IntegrationIcon } from "src/components/integrations/integration-icon";

import {
  type EnrichedActivityItem,
  activityItemLink,
  activityOperation,
  enrichActivityItems,
} from "./activity-feed-data";

const LIMIT = 500;
const LOOKBACK_WINDOW = 30; // days

export const ActivityFeed = () => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [since] = useState(subDays(new Date(), LOOKBACK_WINDOW).toISOString());

  const { data, isLoading } = useHomeActivityFeedQuery(
    {
      since,
      limit: LIMIT,
    },
    {
      refetchOnMount: "always",
      select: enrichActivityItems,
    },
  );

  return (
    <>
      <Button icon={ActivityIcon} variant="secondary" onClick={onOpen}>
        Activity feed
      </Button>
      <Drawer isOpen={isOpen} onClose={onClose} size="md">
        <DrawerHeader>
          <Row justify="space-between" align="center" flex={1}>
            <Row gap={2} align="center">
              <SectionHeading>Activity feed</SectionHeading>
            </Row>
            <IconButton
              aria-label="Close"
              icon={CloseIcon}
              size="sm"
              onClick={onClose}
            />
          </Row>
        </DrawerHeader>
        <DrawerBody p={0}>
          {isLoading ? (
            <Spinner size="lg" mx="auto" mt={6} />
          ) : data?.length ? (
            data.map((activityItem, idx) => (
              <ActivityItem key={idx} item={activityItem} />
            ))
          ) : (
            <Box color="text.secondary" textAlign="center" p={6}>
              No recent activity in this workspace, or you don't have permission
              to view activity.
            </Box>
          )}
        </DrawerBody>
      </Drawer>
    </>
  );
};

const ActivityItem: FC<{ item: EnrichedActivityItem }> = ({ item }) => {
  if (!item || !item.resource || !item.id || !item.created_at) return null;
  return (
    <Row
      h="100px"
      align="center"
      as={RouterLink}
      borderBottomWidth="hairline"
      borderBottomStyle="solid"
      borderColor="base.border"
      to={activityItemLink(item.resource, item.resource_id)}
      gap={2}
      px={6}
      className="group"
      justify="space-between"
      transitionProperty="background-color"
      transitionDuration="faster"
      transitionTimingFunction="easeOutQuad"
      _hover={{ bg: "base.lightBackground", transitionDuration: "fast" }}
    >
      <Column gap={2}>
        <Column>
          <Text fontWeight="medium">
            {item.resource} {activityOperation(item.new, item.old)} by{" "}
            {item.user_name}
          </Text>
          <Text size="sm" color="text.secondary">
            {formatDistance(parseISO(item.created_at), new Date(), {
              addSuffix: true,
            })}
          </Text>
        </Column>
        {item.source && (
          <Row align="center" gap={1}>
            {item.source.icon && (
              <IntegrationIcon
                size={4}
                src={item.source.icon}
                name={item.source.name}
              />
            )}
            <TextWithTooltip>{item.source.name}</TextWithTooltip>
          </Row>
        )}
        {item.destination && (
          <Row align="center" gap={1}>
            {item.destination.icon && (
              <IntegrationIcon
                size={4}
                src={item.destination.icon}
                name={item.destination.name}
              />
            )}
            <TextWithTooltip>{item.destination.name}</TextWithTooltip>
          </Row>
        )}
      </Column>
      <Box
        as={ArrowRightIcon}
        color="text.tertiary"
        boxSize={5}
        opacity={0}
        transitionProperty="opacity"
        transitionDuration="faster"
        transitionTimingFunction="easeOutQuad"
        _groupHover={{ opacity: 1, transitionDuration: "fast" }}
      />
    </Row>
  );
};
